import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { billingService } from '../../services/billingService';
import SubscriptionCard from '../dashboard/SubscriptionCard';
import ConfirmModal from '../common/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SettingsSubscription = () => {
  const { userProfile, refreshUserProfile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const hasSyncedRef = React.useRef(false);

  const planInfo = useMemo(() => {
    const plan = userProfile?.plan || {};
    const active = plan.status === 'active' || userProfile?.saas_usage?.subscription_active;

    // Fallback for nextBilling from financial subscriptions if not in plan
    let nextBilling = plan.nextBilling;
    let stripeSubscriptionId = plan.stripeSubscriptionId;

    if (!nextBilling && userProfile?.financial?.subscriptions?.length > 0) {
      const activeSub = userProfile.financial.subscriptions.find(
        (s) => s.status === 'active' || s.status === 'trialing'
      );
      if (activeSub) {
        nextBilling = activeSub.current_period_end;
        stripeSubscriptionId = activeSub.subscription_id;
      }
    }

    return {
      type: plan.type || (active ? 'pro' : 'free'),
      status: plan.status || (active ? 'active' : 'inactive'),
      isActive: active,
      monthlyCredits: plan.monthlyCredits ?? 0,
      nextBilling: nextBilling,
      stripeCustomerId: plan.stripeCustomerId || userProfile?.legal?.stripe_customer_id,
      stripeSubscriptionId: stripeSubscriptionId,
      cancelAtPeriodEnd: Boolean(plan.cancelAtPeriodEnd),
    };
  }, [userProfile]);

  const handleStartSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      const res = await billingService.startSubscriptionCheckout();
      const url = res?.checkout_url || res?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error(t('subscription.toastStartError'));
    } catch (error) {
      toast.error(error?.message || t('subscription.toastCheckoutError'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      await billingService.cancelSubscription();
      await refreshUserProfile?.();
      setCancelModalOpen(false);
      toast.warning(t('subscription.toastCanceled'), {
        duration: 6000,
        title: t('subscription.toastCanceledTitle'),
      });
    } catch (error) {
      toast.error(error?.message || t('subscription.toastCancelError'));
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSyncSubscription = useCallback(async () => {
    setSyncLoading(true);
    try {
      const res = await billingService.syncSubscription();
      await refreshUserProfile?.();
      const message = res?.status === 'synced'
        ? t('subscription.toastSyncSuccess')
        : `${t('subscription.toastSyncDone')} (${res?.status || 'unknown'}).`;
      toast.success(message, { duration: 4000 });
    } catch (error) {
      toast.error(error?.message || t('subscription.toastSyncError'));
    } finally {
      setSyncLoading(false);
    }
  }, [refreshUserProfile, toast, t]);

  useEffect(() => {
    if (planInfo.isActive && !planInfo.nextBilling && !syncLoading && !hasSyncedRef.current) {
      console.log('Missing nextBilling for active subscription, triggering sync...');
      hasSyncedRef.current = true;
      handleSyncSubscription();
    }
  }, [planInfo.isActive, planInfo.nextBilling, syncLoading, handleSyncSubscription]);

  const handleReactivateSubscription = async () => {
    if (planInfo.cancelAtPeriodEnd && planInfo.stripeSubscriptionId) {
      setSubscriptionLoading(true);
      try {
        await billingService.reactivateSubscription();
        toast.success(t('subscription.toastReactivated'), {
          duration: 5000,
          title: t('subscription.toastReactivatedTitle'),
        });
        await refreshUserProfile();
      } catch (error) {
        toast.error(error?.response?.data?.detail || t('subscription.toastReactivateError'));
      } finally {
        setSubscriptionLoading(false);
      }
    } else {
      navigate('/credits');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('subscription.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
            {t('subscription.subtitle')}
          </p>
        </div>
        <div className="hidden sm:block">
            <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
                <FontAwesomeIcon icon="crown" className="text-xl" />
            </div>
        </div>
      </div>

      <div className="space-y-6">
        <SubscriptionCard
          planInfo={planInfo}
          loading={subscriptionLoading}
          syncLoading={syncLoading}
          onCancel={() => setCancelModalOpen(true)}
          onSync={handleSyncSubscription}
          onSubscribe={handleStartSubscription}
          onReactivate={handleReactivateSubscription}
        />
        
        {/* Cancel Subscription Modal */}
        <ConfirmModal
          isOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={handleCancelSubscription}
          title={t('subscription.cancelTitle')}
          message={
            <div className="space-y-3">
              <p>{t('subscription.cancelMessage')}</p>
              <ul className="space-y-1.5 ml-1">
                <li className="flex items-center gap-2">
                  <FontAwesomeIcon icon="times" className="text-red-500 text-[10px]" />
                  <span>{t('subscription.cancelFeature1')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <FontAwesomeIcon icon="times" className="text-red-500 text-[10px]" />
                  <span>{t('subscription.cancelFeature2')}</span>
                </li>
              </ul>
              <p className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-md p-2 text-amber-800 dark:text-amber-200">
                <FontAwesomeIcon icon="circle-info" className="mr-1.5" />
                {t('subscription.cancelWarning')}
              </p>
            </div>
          }
          confirmText={t('subscription.confirmCancel')}
          cancelText={t('subscription.keepSubscription')}
          variant="warning"
          loading={subscriptionLoading}
        />
      </div>
    </div>
  );
};

export default SettingsSubscription;
